import * as dateFns from 'date-fns';
import * as locale from 'date-fns/locale';


function parseDate(date: string, format?: string): Date {


	if (format)
		return dateFns.parse(date, format, new Date());

	// yyyy-MM-ddTHH:mm:ss(.fff)(+/-HH:mm)
	// eslint-disable-next-line no-useless-escape
	if (date.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([\+|-]\d{2}:\d{2})?/)) {
		const d = date.split(/[^0-9]/).map(x => parseInt(x, 10));
		return new Date(d[0], d[1] - 1, d[2], d[3], d[4], d[5]);
	}

	// dd-MM-yyyy( )(HH:mm(:ss))
	// dd/MM/yyyy( )(HH:mm(:ss))
	// eslint-disable-next-line no-useless-escape
	if (date.match(/^(\d{2}[\/\-]\d{2}[\/\-]\d{4})?[ ]?(\d{2}:\d{2}(:\d{2})?)?$/) && date.trim() !== '') {
		const a = date.split(/[^0-9]/).map(x => parseInt(x, 10));
		return new Date(a[2] || 0, a[1] - 1 || 0, a[0] || 0, a[3] || 0, a[4] || 0, a[5] || 0);
	}

	throw new Error(`La fecha ${date} tiene un formato de fecha no reconocido`);
}


const inferLocale = (): locale.Locale => {
	const localeAsAny = locale as any;

	const posibleLocales = [
		// Pruebo con el idioma del navegador, pero en el formato que aparece
		// como propiedad en el objeto locale
		navigator.language.replace('-', ''),
		// Pruebo con la versión del idioma sin especificar región
		navigator.language.split('-')[0],
		// Como último recurso, me quedo en Español
		'es'
	];

	return posibleLocales.map(loc => localeAsAny[loc]).find(x => x);
};


function parseTime(date: string): Date {

	// HH:mm - De momento no permito segundos porque no hay ningún
	// punto de la aplicación que lo necesite, y permitir segundos
	// hace que la usabilidad del TimeBox quede limitada porque no
	// es fácil diferenciar entre "permito segundos" y no lo permito
	// por la forma en que están montados TimeBox y TextBox.

	if (date.match(/^\d{2}:\d{2}$/)) {

		const hours = parseInt(date.split(':')[0] || '0');
		const minutes = parseInt(date.split(':')[1] || '0');

		if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
			const now = new Date();
			now.setHours(hours, minutes, 0, 0);
			return now;
		}
	}

	throw TypeError('La hora no es válida. El formato debe ser hh:mm.');
}

const formatDate = function (date: Date | string, mask: string) {

	let realDate: Date;

	if (typeof date === 'string') {
		realDate = parseDate(date);
	}
	else {
		realDate = date;
	}

	if (!dateFns.isDate(realDate)) {
		throw TypeError('La fecha no es válida.');
	}

	// dateFns no es precisamente rápido. Optimizo el caso más habitual que tenemos dd/MM/yyyy
	if (mask === 'dd/MM/yyyy') {
		const day = realDate.getDate();
		const month = realDate.getMonth() + 1;
		const year = realDate.getFullYear();
		return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year.toString().padStart(4, '0')}`;
	}

	// Incluyo esta nueva mask para los casos en los que queremos la fecha completa del tipo "1 de enero de 2024"
	if (mask === 'fullDate') {
		return dateFns.format(realDate, 'PPP', { locale: inferLocale() });
	}

	// HACK: no se el por qué pero date-Fns no funciona correctamente cuando tenemos fechas anteriores o iguales a 1900
	// Se supone que cuando nos llega una fecha de 1900 estamos ignorando siempre el año y suelen ser horas
	if (realDate.getFullYear() <= 1900) {
		const originalYear = realDate.getFullYear();
		realDate.setFullYear(2000);
		return dateFns.format(realDate, mask, { locale: inferLocale() }).replace('2000', originalYear.toString());
	}

	return dateFns.format(realDate, mask, { locale: inferLocale() });
};


function getDifferenceInDays(date1: Date, date2: Date) {

	const oneDayInMs = 1000 * 60 * 60 * 24;

	const date1InMs = date1.getTime();
	const date2InMs = date2.getTime();

	const differenceInMs = date1InMs - date2InMs;
	return Math.ceil(differenceInMs / oneDayInMs);
}

function addDays(date: Date, days: number): Date {
	const newDate = new Date(date);
	newDate.setDate(date.getDate() + days);
	return newDate;
}

function areSameDate(d1: Date, d2: Date): boolean {
	const x1 = new Date(d1);
	const x2 = new Date(d2);

	x1.setHours(0, 0, 0, 0);
	x2.setHours(0, 0, 0, 0);

	return x1.getTime() === x2.getTime();
}

function addHours(dateTime: Date, hours: number): Date {
	const newDate = new Date(dateTime);
	newDate.setHours(dateTime.getHours() + hours);
	return newDate;
}

function addMinutes(dateTime: Date, minutes: number): Date {
	const newDate = new Date(dateTime);
	newDate.setMinutes(dateTime.getMinutes() + minutes);
	return newDate;
}

function buildDateTime(date: Date, time: Date, truncToSeconds?: 'TRUNC_TO_SECONDS'): Date {

	const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes());
	if (truncToSeconds) {
		result.setSeconds(0);
		result.setMilliseconds(0);
	}
	return result;
}

function today(): Date {
	return dateFns.startOfDay(new Date());
}

function toStartOfDay(date: Date) {
	return dateFns.startOfDay(date);
}

function nowTruncToSeconds(): Date {
	const now = new Date();
	now.setSeconds(0, 0);
	return now;
}

function getMinutesSince(from: Date): number {
	const now = new Date();

	const oneMinutesInMs = 1000 * 60;
	const nowInMs = now.getTime();
	const fromInMs = from.getTime();

	const differenceInMs = nowInMs - fromInMs;
	return Math.floor(differenceInMs / oneMinutesInMs);
}

export {
	formatDate,
	parseDate,
	parseTime,
	getDifferenceInDays,
	inferLocale,
	addDays,
	areSameDate,
	addHours,
	buildDateTime,
	today,
	nowTruncToSeconds,
	getMinutesSince,
	addMinutes,
	toStartOfDay
};
